/**
 * Room session tracing.
 *
 * Individual room events (`room-created`, `peer-joined`, `room-reconnect-attempted`, …)
 * cannot be stitched into a story in PostHog: nothing tells which events belong
 * to the same visit, in which order they happened, or whether the user simply
 * re-created the room after something broke.
 *
 * This module adds that spine:
 *  - one `room_session_id` per join/create, registered as a PostHog super
 *    property so *every* event of that session carries it,
 *  - a monotonic sequence number and a millisecond offset per trace event,
 *  - detection of a room re-created in the same tab,
 *  - a token bucket so a long or flapping session costs tens of events, not
 *    thousands.
 *
 * The state machine is pure and injectable (clock + sink), so it is unit
 * testable without PostHog or a browser.
 */

import { hashId, onPostHog, trackEvent } from "@/engine/diagnostics/analytics";

import type {
  CorrelatedTrace,
  PeerTraceArgs,
  RoomSessionKind,
  TraceArgs,
  TracePhaseMap,
} from "./messageContracts";
export type { RoomSessionKind, TracePhase } from "./messageContracts";
type EndFields = Pick<
  TracePhaseMap["session-ended"],
  "next_room" | "next_kind"
>;

/** Trace events allowed per minute. Enough for a flapping session, not a flood. */
export const TRACE_BUDGET_PER_MINUTE = 30;
/** A peer that comes back within this window is a reconnect, not a newcomer. */
export const PEER_RETURN_WINDOW_MS = 60_000;

const SESSION_STORAGE_KEY = "campfire:last-room-session";

export interface TraceSink {
  (
    event: "room-trace-recorded",
    properties: CorrelatedTrace,
    dedupKey?: string,
  ): void;
}

export interface RoomTracerOptions {
  now?: () => number;
  sink?: TraceSink;
  /** Called with the super properties every session start/end. */
  register?: (properties: Record<string, unknown>) => void;
  /** Persistence for cross-session re-creation detection. */
  storage?: Pick<Storage, "getItem" | "setItem" | "removeItem">;
  newId?: () => string;
  budgetPerMinute?: number;
}

interface ActiveSession {
  id: string;
  roomId: string;
  peer: string;
  kind: RoomSessionKind;
  startedAt: number;
  seq: number;
  reconnects: number;
  fallbacks: number;
  peers: Set<string>;
  entryResolved: boolean;
}

interface StoredSession {
  roomId: string;
  startedAt: number;
  endedAt?: number;
  endReason?: string;
}

export interface RoomTracer {
  start(roomId: string, peerId: string, kind: RoomSessionKind): string;
  trace(...args: TraceArgs): void;
  /** Peer trace with automatic "returned within the window" classification. */
  tracePeer(...args: PeerTraceArgs): void;
  /**
   * Record what the entry *actually* was once presence settled: joining a code
   * nobody is using is indistinguishable from creating a room, so the intent
   * alone is misleading in analytics.
   */
  resolveEntry(peersAtEntry: number): void;
  end(reason: string, fields?: EndFields): void;
  readonly sessionId: string | null;
}

/**
 * What gets through a spent allowance. Session boundaries, without which a
 * trace is unreadable; and the media plane's own record — each move, and what
 * the SFU reports — which a join's ICE candidates otherwise crowd out of the
 * one minute it matters in (#999). Both are bounded by the session's
 * lifecycle and the SFU's capped retries, not by the allowance.
 */
function exemptFromBudget(args: TraceArgs): boolean {
  // Narrowed on the tuple, not on a destructured phase, so `fields` follows it.
  if (args[0] === "diagnostic-recorded")
    return args[1].diagnostic.startsWith("sfu-");
  return [
    "session-started",
    "session-ended",
    "room-recreated",
    "media-transport-changed",
  ].includes(args[0]);
}

export function createRoomTracer(options: RoomTracerOptions = {}): RoomTracer {
  const now = options.now ?? (() => Date.now());
  const sink =
    options.sink ??
    ((event, properties, dedupKey) => trackEvent(event, properties, dedupKey));
  const register =
    options.register ??
    // Through the queue in `analytics`, so that PostHog stays out of the graph
    // that is fetched before the first paint (#531).
    ((properties) => {
      onPostHog((ph) => ph.register?.(properties));
    });
  const storage = options.storage;
  const newId =
    options.newId ??
    (() =>
      `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`);
  const budget = options.budgetPerMinute ?? TRACE_BUDGET_PER_MINUTE;

  let session: ActiveSession | null = null;
  let windowStart = 0;
  let sentInWindow = 0;
  /** peerId -> when it was last seen leaving, for return detection. */
  const lastSeenLeaving = new Map<string, number>();

  const readStored = (): StoredSession | null => {
    if (!storage) return null;
    try {
      const raw = storage.getItem(SESSION_STORAGE_KEY);
      if (!raw) return null;
      const parsed: StoredSession = JSON.parse(raw);
      return parsed;
    } catch {
      return null;
    }
  };

  const writeStored = (value: StoredSession) => {
    if (!storage) return;
    try {
      storage.setItem(SESSION_STORAGE_KEY, JSON.stringify(value));
    } catch {
      /* storage is a nice-to-have, never a hard dependency */
    }
  };

  const budgetAvailable = (at: number, args: TraceArgs) => {
    if (at - windowStart > 60_000) {
      windowStart = at;
      sentInWindow = 0;
    }
    if (!exemptFromBudget(args) && sentInWindow >= budget) return false;
    sentInWindow += 1;
    return true;
  };

  // `active` is passed in rather than read from the closure: every caller has
  // already established a session, and threading it keeps that fact in the types
  // without a non-null assertion.
  const emit = (active: ActiveSession, ...args: TraceArgs) => {
    const [phase, fields] = args;
    const at = now();
    if (!budgetAvailable(at, args)) return;
    active.seq += 1;
    sink(
      "room-trace-recorded",
      // TraceArgs already correlates phase and fields; object spread loses that
      // correlation in TypeScript, but does not change the validated tuple.
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      {
        phase,
        seq: active.seq,
        room_session_id: active.id,
        room: active.roomId,
        peer: active.peer,
        ms_since_start: at - active.startedAt,
        online: typeof navigator === "undefined" ? true : navigator.onLine,
        ...fields,
      } as CorrelatedTrace,
      // The sequence number keeps repeated phases out of the 10s dedup window.
      `${active.id}:${active.seq}`,
    );
  };

  const closeSession = (reason: string, fields: EndFields = {}) => {
    if (!session) return;
    const at = now();
    emit(session, "session-ended", {
      reason,
      duration_ms: at - session.startedAt,
      reconnects: session.reconnects,
      fallbacks: session.fallbacks,
      peers_seen: session.peers.size,
      entry_resolved: session.entryResolved,
      ...fields,
    });
    writeStored({
      roomId: session.roomId,
      startedAt: session.startedAt,
      endedAt: at,
      endReason: reason,
    });
    register({ room_session_id: null, room: null });
    session = null;
  };

  return {
    get sessionId() {
      return session?.id ?? null;
    },

    start(roomId, peerId, kind) {
      // Starting a room while another session is open would orphan the old
      // one — no `session-ended`, and every later event under the new
      // correlation id. Close it explicitly so each session has both ends.
      if (session)
        closeSession("superseded", { next_room: roomId, next_kind: kind });

      const at = now();
      const previous = readStored();
      const id = newId();
      const peer = hashId(peerId);

      session = {
        id,
        roomId,
        peer,
        kind,
        startedAt: at,
        seq: 0,
        reconnects: 0,
        fallbacks: 0,
        peers: new Set(),
        entryResolved: false,
      };
      lastSeenLeaving.clear();

      register({ room_session_id: id, room: roomId, peer });

      // The same tab starting another room means the previous one was left or
      // abandoned — the interesting case is "abandoned and immediately redone".
      if (previous && previous.roomId) {
        emit(session, "room-recreated", {
          previous_room: previous.roomId,
          previous_duration_ms: (previous.endedAt ?? at) - previous.startedAt,
          previous_end_reason: previous.endReason ?? "abandoned",
          same_room: previous.roomId === roomId,
          ms_since_previous: previous.endedAt ? at - previous.endedAt : null,
        });
      }

      writeStored({ roomId, startedAt: at });
      emit(session, "session-started", { kind });
      return id;
    },

    trace(...args) {
      const [phase] = args;
      if (!session) return;
      if (phase === "reconnect-attempted") session.reconnects += 1;
      if (phase === "video-transport-fallback-started") session.fallbacks += 1;
      emit(session, ...args);
    },

    tracePeer(...[phase, peerId, fields]: PeerTraceArgs) {
      if (!session) return;
      const at = now();
      const peer = hashId(peerId);

      if (phase === "peer-joined") {
        const left = lastSeenLeaving.get(peerId);
        const awayMs =
          left !== undefined && at - left <= PEER_RETURN_WINDOW_MS
            ? at - left
            : undefined;
        const returned = awayMs !== undefined;
        session.peers.add(peerId);
        emit(session, returned ? "peer-returned" : "peer-joined", {
          remote_peer: peer,
          away_ms: awayMs,
          known_peers: session.peers.size,
          ...fields,
        });
        if (returned) lastSeenLeaving.delete(peerId);
        return;
      }

      lastSeenLeaving.set(peerId, at);
      emit(session, phase, {
        remote_peer: peer,
        known_peers: session.peers.size,
        ...fields,
      });
    },

    resolveEntry(peersAtEntry) {
      if (!session || session.entryResolved) return;
      session.entryResolved = true;
      const joinedExisting = peersAtEntry > 0;
      emit(session, "room-entry-resolved", {
        intent: session.kind,
        peers_at_entry: peersAtEntry,
        resolved_as: joinedExisting ? "joined_existing" : "created_empty",
        // The interesting case: user typed a code but nobody was there.
        empty_join: session.kind === "join" && !joinedExisting,
      });
    },

    end(reason, fields = {}) {
      closeSession(reason, fields);
    },
  };
}

/** Tracer used by the app. Tests build their own with `createRoomTracer`. */
export const roomTracer = createRoomTracer({
  storage: typeof window === "undefined" ? undefined : window.sessionStorage,
});

/** Convenience wrappers so call sites stay one short line. */
export const startRoomSession = (
  roomId: string,
  peerId: string,
  kind: RoomSessionKind,
) => roomTracer.start(roomId, peerId, kind);

export const traceRoom = (...args: TraceArgs) => roomTracer.trace(...args);

export const traceRoomPeer = (...args: PeerTraceArgs) =>
  roomTracer.tracePeer(...args);

export const resolveRoomEntry = (peersAtEntry: number) =>
  roomTracer.resolveEntry(peersAtEntry);

export const endRoomSession = (reason: string, fields?: EndFields) =>
  roomTracer.end(reason, fields);
